import React from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';

export interface FormCardMessage {
  type: 'success' | 'error' | 'info';
  text: string;
}

export interface FormCardProps {
  /** Form title */
  title: string;
  /** Optional subtitle below title */
  subtitle?: string;
  /** Title size variant */
  titleSize?: 'sm' | 'lg';
  /** Success/error message to display */
  message?: FormCardMessage | null;
  /** Form content */
  children: React.ReactNode;
  /** Additional className for container */
  className?: string;
  /** Optional close handler - renders X icon in top right corner */
  onClose?: () => void;
}

/**
 * FormCard - Base component for forms (login, signup, profile edit, etc.)
 *
 * Provides consistent styling for:
 * - Title and subtitle header
 * - Success/error message display
 * - Form content wrapper
 *
 * Usage:
 * <FormCard title="Login" subtitle="Enter your credentials" message={error}>
 *   <form>...</form>
 * </FormCard>
 */
export const FormCard: React.FC<FormCardProps> = ({
  title,
  subtitle,
  titleSize = 'lg',
  message,
  children,
  className,
  onClose,
}) => {
  return (
    <div className={classNames(styles.formCard, className)}>
      {onClose && (
        <button
          className={styles.closeBtn}
          onClick={onClose}
          type="button"
          aria-label="Close"
          data-testid="form-card-close"
        >
          <i className="icon-xmark" />
        </button>
      )}
      <div className="d-flex flex-column g-4">
        <h1 className={classNames(styles.title, styles[`title--${titleSize}`])}>
          {title}
        </h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>

      {message && (
        <div className={classNames(styles.message, styles[`message--${message.type}`])}>
          <span>{message.type === 'success' ? '✅' : message.type === 'info' ? 'ℹ️' : '⚠️'}</span>
          <span>{message.text}</span>
        </div>
      )}

      {children}
    </div>
  );
};

export default FormCard;
